import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { z } from "zod";
import { customerSignUpInputSchema, OtpReason } from "muminsmart-utils";
import {
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormControl,
  Form,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import SignupLayout from "./SignupLayout";
import PhoneInputField from "@/components/common/PhoneInputField";
import { customerSignup } from "@/lib/api/auth-actions";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import AuthFormSkeleton from "../../common/skeletons/AuthFormSkeleton";
import { routes } from "@/lib/constants";
import ContactOtpVerification, {
  OtpVerificationState,
  normalizePhoneNumber,
} from "./ContactOtpVerification";
import UserTermsAndConditionsDialog from "./UserTermsAndConditionsDialog";
import UserPrivacyPolicyDialog from "./UserPrivacyPolicyDialog";

function CustomerSignupForm() {
  const { data: session, status, login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const form = useForm<z.infer<typeof customerSignUpInputSchema>>({
    resolver: zodResolver(customerSignUpInputSchema),
    defaultValues: {
      fullName: "",
      phoneNumber: "",
      its: "",
      referralCode: searchParams.get("referralCode")?.trim() ?? "",
      password: "",
    },
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [hasAcceptedPolicies, setHasAcceptedPolicies] = useState(false);
  const [consentError, setConsentError] = useState<string | null>(null);
  const [isPrivacyDialogOpen, setIsPrivacyDialogOpen] = useState(false);
  const [phoneVerification, setPhoneVerification] =
    useState<OtpVerificationState>({
      isVerified: false,
      verifiedOtp: null,
      value: "",
    });
  const watchedPhoneNumber = form.watch("phoneNumber");
  const normalizedPhoneNumber = normalizePhoneNumber(watchedPhoneNumber || "");

  useEffect(() => {
    if (session && session.user) {
      navigate(routes.home);
    }
  }, [navigate, session]);

  const onSubmit = async (data: z.infer<typeof customerSignUpInputSchema>) => {
    if (!normalizedPhoneNumber || !phoneVerification.isVerified) {
      toast.error("Please verify your phone number before continuing.");
      return;
    }

    if (!hasAcceptedPolicies) {
      setConsentError(
        "Please accept the Terms and Conditions and Privacy Policy.",
      );
      toast.error("Please accept the Terms and Conditions and Privacy Policy.");
      return;
    }

    setIsSubmitting(true);

    await customerSignup({
      ...data,
      phoneNumber: normalizedPhoneNumber,
    })
      .then((res) => {
        if (res.success) {
          toast.success("User registered. Welcome to the marketplace!");
          // Login the user and redirect to home page
          return login({
            mobile: normalizedPhoneNumber,
            password: data.password,
          })
            .then((res) => {
              if (!res.ok) {
                toast.error("Login failed. Please try again.");
              }
            })
            .catch((error) => toast.error(error.message))
            .finally(() => setIsSubmitting(false));
        } else {
          toast.error(
            res.message || "User registration failed. Please try again.",
          );
        }
      })
      .catch((error) => toast.error(error.message))
      .finally(() => setIsSubmitting(false));
  };

  if (status === "loading") {
    return <AuthFormSkeleton />;
  }

  return (
    <SignupLayout type="customer">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5 px-4">
          <FormField
            control={form.control}
            name="fullName"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-medium">Full Name</FormLabel>
                <FormControl>
                  <Input
                    placeholder="John Doe"
                    className="rounded-full p-5 border-muted bg-white"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="phoneNumber"
            render={() => (
              <FormItem>
                <ContactOtpVerification
                  label="WhatsApp Number"
                  value={normalizedPhoneNumber}
                  channel="mobile"
                  reason={OtpReason.MOBILE_VERIFICATION}
                  disabled={isSubmitting}
                  verifiedText="WhatsApp number verified"
                  onValidateField={() => form.trigger("phoneNumber")}
                  onVerifiedStateChange={setPhoneVerification}
                >
                  <FormControl>
                    <Controller
                      control={form.control}
                      name="phoneNumber"
                      render={({ field }) => (
                        <PhoneInputField
                          country={"in"}
                          value={field.value}
                          onChange={(value) => field.onChange(value)}
                          inputClass={`!w-full !rounded-full !h-12 !pl-14 !pr-4 !text-base ${
                            form.formState.errors.phoneNumber
                              ? "!border-destructive"
                              : ""
                          }`}
                          buttonClass="!border-none !bg-transparent [&_.selected-flag]:!bg-transparent !pl-3"
                          containerClass="!w-full"
                          dropdownClass="!rounded-md"
                        />
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </ContactOtpVerification>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="its"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-medium">ITS No</FormLabel>
                <FormControl>
                  <Input
                    placeholder="***********"
                    className="rounded-full p-5 border-muted bg-white"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-medium">Email Id</FormLabel>
                <FormControl>
                  <Input
                    placeholder="example@gmail.com"
                    className="rounded-full p-5 border-muted bg-white"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-medium">Password</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="********"
                      className="rounded-full p-5 border-muted bg-white pr-10"
                      {...field}
                      aria-invalid={
                        form.formState.errors.password ? "true" : "false"
                      }
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted"
                    >
                      {showPassword ? <EyeOff size={30} /> : <Eye size={30} />}
                    </button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="referralCode"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-medium">
                  Referral Code (Optional)
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder="XXXXXXXX"
                    className="rounded-full p-5 border-muted bg-white"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="space-y-2">
            <div className="flex items-start gap-3 rounded-full border border-muted bg-white p-3">
              <Checkbox
                id="accept-policies"
                checked={hasAcceptedPolicies}
                onCheckedChange={(checked) => {
                  const isChecked = checked === true;
                  setHasAcceptedPolicies(isChecked);
                  if (isChecked) {
                    setConsentError(null);
                  }
                }}
                className="mt-0.5"
              />
              <label
                htmlFor="accept-policies"
                className="text-sm text-[#3f3f3f] leading-6"
              >
                I agree to the <UserTermsAndConditionsDialog /> and{" "}
                <UserPrivacyPolicyDialog />
                {"."}
              </label>
            </div>
            {consentError ? (
              <p className="text-sm text-destructive">{consentError}</p>
            ) : null}
          </div>

          <Button
            type="submit"
            className="w-full rounded-full bg-primary py-6"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Please wait..." : "Sign Up"}
          </Button>
        </form>
      </Form>

      <Dialog open={isPrivacyDialogOpen} onOpenChange={setIsPrivacyDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85dvh] rounded-3xl">
          <DialogHeader>
            <DialogTitle>Privacy Policy (User)</DialogTitle>
            <DialogDescription>
              Reference document: privacy-policy-user.md
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto pr-1 space-y-3 text-sm text-[#404040] leading-6">
            <p>
              We collect only the data needed for account creation, order
              fulfillment, security, and legal compliance.
            </p>
            <p>
              ITS details are collected only for community verification and are
              not shared with third parties except where required by law.
            </p>
            <p>
              You can request correction, update, or erasure of personal data as
              per applicable Indian law.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </SignupLayout>
  );
}

export default CustomerSignupForm;
